import { useState } from "react";
import { Plus, Pencil, Trash2, ArrowLeft, GraduationCap, Presentation } from "lucide-react";
import { useList } from "../../pages/admin/AdminApp";
import { useCrud } from "../../lib/adminApi";
import { tr } from "../../lib/i18n";
import { Field, I18nInput, ImageUpload } from "./Fields";
import RichEditor from "./RichEditor";
import SortableList from "./SortableList";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const KINDS = { received: { label: "Aldığım eğitimler & sertifikalar", icon: GraduationCap }, given: { label: "Verdiğim eğitimler", icon: Presentation } };

function TrainingForm({ item, onBack }) {
  const [f, setF] = useState(item);
  const [busy, setBusy] = useState(false);
  const { save } = useCrud("trainings");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await save(f);
    setBusy(false);
    if (ok) onBack();
  };
  return (
    <form onSubmit={submit} className="bg-stone-50 border rounded-xl p-5 space-y-4" data-testid="training-form">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm">{item.id ? "Eğitimi düzenle" : "Yeni eğitim"}</p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onBack} data-testid="training-form-back"><ArrowLeft size={14} className="mr-1" />Vazgeç</Button>
          <Button type="submit" size="sm" disabled={busy} data-testid="training-form-save">{busy ? "…" : "Kaydet"}</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Bölüm">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={f.kind || "received"} onChange={(e) => set("kind")(e.target.value)} data-testid="training-kind">
            <option value="received">Aldığım eğitim / sertifika</option>
            <option value="given">Verdiğim eğitim</option>
          </select>
        </Field>
        <Field label="Tarih / Yıl" hint="Örn. 2023 veya Mart 2023"><Input value={f.date || ""} onChange={(e) => set("date")(e.target.value)} data-testid="training-date" placeholder="2023" /></Field>
      </div>
      <I18nInput label="Başlık" value={f.title} onChange={set("title")} testid="training-title" maxLength={200} />
      <I18nInput label="Kısa açıklama (kartta görünür)" value={f.description} onChange={set("description")} multiline rows={3} testid="training-description" maxLength={1200} />
      <div className="grid sm:grid-cols-2 gap-3">
        <I18nInput label="Kurum / Düzenleyen (isteğe bağlı)" value={f.org} onChange={set("org")} testid="training-org" maxLength={160} />
        <I18nInput label="Seviye / Kategori (isteğe bağlı)" value={f.level} onChange={set("level")} testid="training-level" maxLength={80} />
        <Field label="Konum (isteğe bağlı)"><Input value={f.location || ""} onChange={(e) => set("location")(e.target.value)} data-testid="training-location" placeholder="Online / İstanbul" /></Field>
        <Field label="Süre (isteğe bağlı)"><Input value={f.duration_text || ""} onChange={(e) => set("duration_text")(e.target.value)} data-testid="training-duration" placeholder="8 hafta" /></Field>
      </div>
      <div className="border-t pt-4 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Detay sayfası</p>
        <RichEditor label="Uzun açıklama (detay sayfasında görünür)" value={f.body} onChange={set("body")} testid="training-body" minH="min-h-[140px]" />
        <ImageUpload label="Sertifika görseli (detay sayfasında büyük gösterilir)" value={f.certificate} onChange={set("certificate")} testid="training-certificate" alt="sertifika" />
      </div>
      <Field label="Bağlantı (isteğe bağlı)" hint="Kayıt linki ya da sertifika doğrulama adresi (http/https)"><Input value={f.link || ""} onChange={(e) => set("link")(e.target.value)} data-testid="training-link" placeholder="https://" /></Field>
      <ImageUpload label="Kart görseli (isteğe bağlı)" value={f.cover} onChange={set("cover")} testid="training-cover" alt={f.title?.tr} />
      <label className="flex items-center gap-3 text-sm text-stone-600"><Switch checked={f.status !== "draft"} onCheckedChange={(v) => set("status")(v ? "published" : "draft")} data-testid="training-status" />Yayında</label>
    </form>
  );
}

function RowBody({ it, onEdit, onRemove }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{tr(it.title) || "(başlıksız)"}</p>
        <p className="text-xs text-stone-400 truncate">{[it.date, tr(it.org), it.location].filter(Boolean).join(" · ")}</p>
      </div>
      {it.status === "draft" && <span className="text-[10px] bg-stone-200 text-stone-600 rounded px-1.5 py-0.5">Taslak</span>}
      {it.certificate && <span className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">Sertifika</span>}
      <button type="button" onClick={() => onEdit(it)} className="text-stone-500 hover:text-stone-900 p-1" data-testid={`training-edit-${it.id}`}><Pencil size={14} /></button>
      <button type="button" onClick={() => onRemove(it.id, tr(it.title))} className="text-red-400 hover:text-red-600 p-1" data-testid={`training-delete-${it.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

export default function TrainingsManager() {
  const { data = [] } = useList("trainings");
  const { remove, reorder } = useCrud("trainings");
  const [editing, setEditing] = useState(null);
  const blank = (kind) => ({ kind, title: { tr: "" }, description: { tr: "" }, body: { tr: "" }, status: "published" });
  const groups = { received: data.filter((d) => (d.kind || "received") === "received"), given: data.filter((d) => d.kind === "given") };
  const onReorder = (kind) => (next) => reorder(kind === "received" ? [...next, ...groups.given] : [...groups.received, ...next]);
  return (
    <div className="bg-white border rounded-xl p-5 mt-6 space-y-6" data-testid="trainings-manager">
      <div>
        <p className="font-semibold">Eğitimler</p>
        <p className="text-sm text-stone-500">Zaman çizelgesi olarak gösterilir. Sürükleyerek sıralayabilirsin; boş bıraktığın alanlar gösterilmez.</p>
      </div>
      {editing ? (
        <TrainingForm item={editing} onBack={() => setEditing(null)} />
      ) : (
        ["received", "given"].map((kind) => {
          const { label, icon: Icon } = KINDS[kind];
          return (
            <div key={kind} className="space-y-2" data-testid={`training-group-${kind}`}>
              <div className="flex items-center gap-2 text-sm font-semibold text-stone-700"><Icon size={16} className="text-amber-600" />{label} <span className="text-stone-400 font-normal">({groups[kind].length})</span></div>
              {groups[kind].length === 0 && <p className="text-xs text-stone-400 pl-6">Henüz eğitim eklenmedi.</p>}
              {groups[kind].length > 0 && (
                <SortableList items={groups[kind]} onReorder={onReorder(kind)} render={(it) => <RowBody it={it} onEdit={setEditing} onRemove={remove} />} />
              )}
              <Button type="button" size="sm" variant="outline" onClick={() => setEditing(blank(kind))} data-testid={`add-training-${kind}`}><Plus size={14} className="mr-1" />Bu bölüme ekle</Button>
            </div>
          );
        })
      )}
    </div>
  );
}
